import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  Stack,
  Title,
  Text,
  Card,
  SimpleGrid,
  Badge,
  Skeleton,
  Group,
  Progress,
  ThemeIcon,
  TextInput,
  SegmentedControl,
  Select,
  ActionIcon,
  Tooltip,
  Box,
  Pagination,
  AspectRatio,
} from '@mantine/core';
import {
  IconTrophy,
  IconPlayerPlay,
  IconFlame,
  IconSearch,
  IconLayoutList,
  IconLayoutGrid,
  IconArrowRight,
  IconBookmarks,
} from '@tabler/icons-react';
import {
  useUserCourseProgressService,
  usePublicCoursesService,
  type CourseProgressComputedModelView,
  type CourseModelView,
} from '@actschurch/ccms-sdk';
import { courseHref } from '@/utils/slug';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';

/**
 * Signed-in account page — "My learning". Lists every course the viewer has
 * opened along with their progress, with controls designed to stay usable
 * once the list grows past a hundred entries:
 *
 *   - search box (matches against course name)
 *   - status filter (All / In progress / Completed / Not started)
 *   - sort dropdown (last accessed / name / percent / sessions remaining)
 *   - list ↔ grid view toggle (grid hidden on very narrow screens)
 *   - pagination at 12 rows per page
 *
 * Anonymous visitors are bounced to the login page (preserving where they
 * came from) before any progress fetch can fire.
 */

const PAGE_SIZE = 12;

type StatusFilter = 'all' | 'in-progress' | 'completed' | 'not-started';
type ViewMode = 'list' | 'grid';
type SortKey = 'last-accessed' | 'name' | 'progress-desc' | 'progress-asc' | 'remaining';

interface EnrichedRow {
  progress: CourseProgressComputedModelView;
  course: CourseModelView | null;
  totalSessions: number;
  completedSessions: number;
  pct: number;
  completed: boolean;
  notStarted: boolean;
  inProgress: boolean;
}

export default function Account() {
  const { isAuthenticated } = useOptionalAuth();
  const location = useLocation();
  const progressService = useUserCourseProgressService();
  const publicCoursesService = usePublicCoursesService();

  // Computed progress: completion + session counts are derived server-side
  // from per-session progress against each course's current session set, so
  // "X of Y sessions" and "Completed" are always accurate (and a course drops
  // out of "Completed" automatically when a new session is added).
  const { data: progress = [], isLoading } = useQuery({
    queryKey: ['my-progress-computed'],
    queryFn: () => progressService.getMyComputed(),
    enabled: isAuthenticated,
  });
  // CourseModelView gives us name + thumbnail + category/ministry labels for
  // each touched course in one round trip.
  const { data: catalog = [] } = useQuery({
    queryKey: ['public', 'courses', 'view', 'for-account'],
    queryFn: () => publicCoursesService.listView(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // UI state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('last-accessed');
  const [view, setView] = useState<ViewMode>('list');
  const [page, setPage] = useState(1);

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?redirect=${redirect}`} replace />;
  }

  const courseById = new Map(catalog.map((c) => [c.courseID, c]));

  // Pair each computed-progress row with its catalog course (name / thumbnail
  // / labels). Completion and session counts come straight from the computed
  // row — no client-side counting or catalog fallbacks needed.
  const enriched: EnrichedRow[] = progress.map((p) => {
    const course = courseById.get(p.courseID) ?? null;
    const totalSessions = p.totalSessionCount;
    const completedSessions = p.completedSessionCount;
    const pct = Math.min(100, Math.max(0, Math.round(Number(p.percentComplete ?? 0))));
    const completed = p.isCompleted;
    const notStarted = !completed && completedSessions === 0;
    return {
      progress: p,
      course,
      totalSessions,
      completedSessions,
      pct,
      completed,
      notStarted,
      inProgress: !completed && !notStarted,
    };
  });

  const totals = {
    completed: enriched.filter((r) => r.completed).length,
    inProgress: enriched.filter((r) => r.inProgress).length,
    notStarted: enriched.filter((r) => r.notStarted).length,
    total: enriched.length,
  };

  // Filter + sort
  const filtered = enriched
    .filter((r) => {
      if (statusFilter === 'in-progress' && !r.inProgress) return false;
      if (statusFilter === 'completed' && !r.completed) return false;
      if (statusFilter === 'not-started' && !r.notStarted) return false;
      if (search.trim()) {
        const needle = search.trim().toLowerCase();
        const name = r.course?.name?.toLowerCase() ?? `course #${r.progress.courseID}`;
        if (!name.includes(needle)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return (a.course?.name ?? '').localeCompare(b.course?.name ?? '');
        case 'progress-desc':
          return b.pct - a.pct;
        case 'progress-asc':
          return a.pct - b.pct;
        case 'remaining': {
          const rA = a.totalSessions - a.completedSessions;
          const rB = b.totalSessions - b.completedSessions;
          return rB - rA;
        }
        case 'last-accessed':
        default:
          return Number(b.progress.lastAccessedAt ?? 0) - Number(a.progress.lastAccessedAt ?? 0);
      }
    });

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={1}>My learning</Title>
        <Text c="dimmed">Pick up where you left off, or finish off a course on the home stretch.</Text>
      </Stack>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <StatCard icon={<IconFlame size={20} />} label="In progress" value={totals.inProgress} color="orange" />
        <StatCard icon={<IconTrophy size={20} />} label="Completed" value={totals.completed} color="teal" />
        <StatCard icon={<IconPlayerPlay size={20} />} label="Total courses touched" value={totals.total} color="brand" />
      </SimpleGrid>

      {/* Filter + view controls */}
      <Card withBorder padding="sm" radius="md">
        <Stack gap="sm">
          <Group gap="sm" wrap="wrap" align="flex-end">
            <TextInput
              placeholder="Search by course name…"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
              style={{ flex: '1 1 220px', minWidth: 200 }}
            />
            <Select
              label={undefined}
              placeholder="Sort"
              data={[
                { value: 'last-accessed', label: 'Last accessed' },
                { value: 'name', label: 'Course name (A→Z)' },
                { value: 'progress-desc', label: 'Most progress' },
                { value: 'progress-asc', label: 'Least progress' },
                { value: 'remaining', label: 'Sessions remaining' },
              ]}
              value={sortKey}
              onChange={(v) => v && setSortKey(v as SortKey)}
              comboboxProps={{ withinPortal: true }}
              style={{ flex: '0 0 220px' }}
            />
            {/* View toggle — icon-only on mobile (larger touch target), icon + label on desktop. */}
            <SegmentedControl
              hiddenFrom="sm"
              size="md"
              value={view}
              onChange={(v) => setView(v as ViewMode)}
              data={[
                {
                  value: 'list',
                  label: (
                    <Tooltip label="List view">
                      <Group gap={0} justify="center" wrap="nowrap" px={6}>
                        <IconLayoutList size={18} />
                      </Group>
                    </Tooltip>
                  ),
                },
                {
                  value: 'grid',
                  label: (
                    <Tooltip label="Grid view">
                      <Group gap={0} justify="center" wrap="nowrap" px={6}>
                        <IconLayoutGrid size={18} />
                      </Group>
                    </Tooltip>
                  ),
                },
              ]}
            />
            <SegmentedControl
              visibleFrom="sm"
              size="sm"
              value={view}
              onChange={(v) => setView(v as ViewMode)}
              data={[
                {
                  value: 'list',
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <IconLayoutList size={14} />
                      <span>List</span>
                    </Group>
                  ),
                },
                {
                  value: 'grid',
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <IconLayoutGrid size={14} />
                      <span>Grid</span>
                    </Group>
                  ),
                },
              ]}
            />
          </Group>

          <SegmentedControl
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v as StatusFilter);
              setPage(1);
            }}
            data={[
              { value: 'all', label: `All (${totals.total})` },
              { value: 'in-progress', label: `In progress (${totals.inProgress})` },
              { value: 'completed', label: `Completed (${totals.completed})` },
              { value: 'not-started', label: `Not started (${totals.notStarted})` },
            ]}
            fullWidth
            size="xs"
          />
        </Stack>
      </Card>

      {/* Body */}
      {isLoading ? (
        <Stack gap="xs">
          <Skeleton height={88} radius="md" />
          <Skeleton height={88} radius="md" />
          <Skeleton height={88} radius="md" />
        </Stack>
      ) : filtered.length === 0 ? (
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="xs">
            <ThemeIcon variant="light" color="gray" size={42} radius="xl">
              <IconBookmarks size={20} />
            </ThemeIcon>
            <Text fw={600}>No courses match the current filter.</Text>
            <Text c="dimmed" size="sm" ta="center">
              {progress.length === 0
                ? "You haven't opened any courses yet — start one from the catalog."
                : 'Clear the search or pick a different status to see more results.'}
            </Text>
          </Stack>
        </Card>
      ) : view === 'grid' ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {pageRows.map((row) => (
            <ProgressGridCard key={row.progress.courseID} row={row} />
          ))}
        </SimpleGrid>
      ) : (
        <Stack gap="sm">
          {pageRows.map((row) => (
            <ProgressListCard key={row.progress.courseID} row={row} />
          ))}
        </Stack>
      )}

      {/* Pagination — only show once we cross the page-size threshold */}
      {pageCount > 1 && (
        <Group justify="center">
          <Pagination
            value={safePage}
            onChange={setPage}
            total={pageCount}
            siblings={1}
            boundaries={1}
            size="sm"
          />
        </Group>
      )}
    </Stack>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" wrap="nowrap">
        <ThemeIcon variant="light" color={color} size={36} radius="xl">
          {icon}
        </ThemeIcon>
        <Stack gap={0} align="flex-end">
          <Text size="xl" fw={700}>
            {value.toLocaleString()}
          </Text>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
}

function sessionsLabel(row: EnrichedRow): string {
  // totalSessions is the course's current non-draft session count, computed
  // server-side — always a real number (0 when the course has no sessions).
  if (row.totalSessions === 0) return 'No sessions yet';
  return `${row.completedSessions} of ${row.totalSessions} session${row.totalSessions === 1 ? '' : 's'}`;
}

function ProgressListCard({ row }: { row: EnrichedRow }) {
  const href = row.course ? courseHref(row.course) : '/courses';
  const courseName = row.course?.name ?? `Course #${row.progress.courseID}`;

  return (
    <Card
      component={Link}
      to={href}
      withBorder
      padding="md"
      radius="md"
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      {/* Desktop: name + progress on the left, percent + sessions on the right */}
      <Group visibleFrom="sm" justify="space-between" wrap="nowrap" align="center" gap="md">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text fw={600} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
              {courseName}
            </Text>
            <StatusBadge row={row} />
          </Group>
          <Progress value={row.pct} size="sm" radius="xl" color={progressColor(row)} />
          <Group gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">{sessionsLabel(row)}</Text>
            {row.course?.categoryName && (
              <Badge size="xs" variant="light" color={row.course.categoryColor ?? 'gray'}>
                {row.course.categoryName}
              </Badge>
            )}
            {row.course?.ministryName && (
              <Badge size="xs" variant="light" color={row.course.ministryColor ?? 'brand'}>
                {row.course.ministryName}
              </Badge>
            )}
          </Group>
        </Stack>
        <Stack gap={2} align="flex-end" miw={80}>
          <Text size="lg" fw={700}>{row.pct.toFixed(0)}%</Text>
          <Tooltip label="Open course">
            <ActionIcon variant="light" radius="xl">
              <IconArrowRight size={16} />
            </ActionIcon>
          </Tooltip>
        </Stack>
      </Group>

      {/* Mobile: stacked */}
      <Stack hiddenFrom="sm" gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text fw={600} lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
            {courseName}
          </Text>
          <Text size="md" fw={700} style={{ flexShrink: 0 }}>{row.pct.toFixed(0)}%</Text>
        </Group>
        <Progress value={row.pct} size="sm" radius="xl" color={progressColor(row)} />
        <Group justify="space-between" wrap="wrap" gap={4}>
          <Text size="xs" c="dimmed">{sessionsLabel(row)}</Text>
          <StatusBadge row={row} />
        </Group>
      </Stack>
    </Card>
  );
}

function ProgressGridCard({ row }: { row: EnrichedRow }) {
  const href = row.course ? courseHref(row.course) : '/courses';
  const courseName = row.course?.name ?? `Course #${row.progress.courseID}`;
  const thumb = row.course?.thumbnail;

  return (
    <Card
      component={Link}
      to={href}
      withBorder
      padding={0}
      radius="md"
      style={{ textDecoration: 'none', color: 'inherit', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <AspectRatio ratio={16 / 9}>
        {thumb ? (
          <img src={thumb} alt={courseName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Box
            style={{
              width: '100%',
              height: '100%',
              background: row.course?.color
                ? `linear-gradient(135deg, ${row.course.color}aa, var(--mantine-color-brand-6))`
                : 'linear-gradient(135deg, var(--mantine-color-brand-5), var(--mantine-color-brand-7))',
            }}
          />
        )}
      </AspectRatio>
      <Stack gap="xs" p="md" style={{ flex: 1 }}>
        <Group gap="xs" wrap="nowrap">
          <Text fw={600} lineClamp={2} style={{ flex: 1, minWidth: 0 }}>{courseName}</Text>
          <StatusBadge row={row} />
        </Group>
        <Progress value={row.pct} size="sm" radius="xl" color={progressColor(row)} />
        <Group justify="space-between" wrap="wrap" gap={4}>
          <Text size="xs" c="dimmed">{sessionsLabel(row)}</Text>
          <Text size="xs" c="dimmed" fw={600}>{row.pct.toFixed(0)}%</Text>
        </Group>
      </Stack>
    </Card>
  );
}

function StatusBadge({ row }: { row: EnrichedRow }) {
  if (row.completed) {
    return <Badge color="teal" variant="light" size="sm">Completed</Badge>;
  }
  if (row.inProgress) {
    return (
      <Badge color="orange" variant="light" size="sm">
        In progress
      </Badge>
    );
  }
  return <Badge color="gray" variant="light" size="sm">Not started</Badge>;
}

function progressColor(row: EnrichedRow): string {
  if (row.completed) return 'teal';
  if (row.inProgress) return 'orange';
  return 'gray';
}
